import { call, put, take, fork, takeLatest, delay } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import {
    setOnlineStatus,
    setOutboxCount,
    replayStart,
    replaySuccess,
    replayFailure,
    triggerOutboxReplay,
} from '../slices/offlineSlice';
import { db } from '../../indexedDB';
import { getKeycloak } from '../../keycloak';
import { getOfflineAction } from '../../helpers/actionRegistry';
import { logger } from 'helpers/logger';
import store from '../index';

function getActionQueueEntries() {
    return db.action_queue
        .where('status')
        .notEqual('failed')
        .sortBy('timestamp');
}

function deleteActionQueueEntry(id) {
    return db.action_queue.delete(id);
}

function updateActionQueueEntry(id, updates) {
    return db.action_queue.update(id, updates);
}

function dispatchAndAwait(actionCreator, payload) {
    // store.dispatch(createAsyncThunk(...)) returns a Promise that resolves
    // to the fulfilled or rejected action — we use this to know the outcome
    return store.dispatch(actionCreator(payload));
}

function* replayActionQueue() {
    const entries = yield call(getActionQueueEntries);
    if (!entries.length) return;

    for (const entry of entries) {
        const actionCreator = getOfflineAction(entry.actionName);
        if (!actionCreator) {
            const attempts = (entry.attempts || 0) + 1;
            if (attempts >= 3) {
                logger.error(`❌ No registered action for: ${entry.actionName} after ${attempts} attempts — discarding`);
                yield call(deleteActionQueueEntry, entry.id);
            } else {
                yield call(updateActionQueueEntry, entry.id, { attempts });
                logger.warn(`⚠️ No registered action for: ${entry.actionName} — attempt ${attempts}/3, keeping`);
            }
            continue;
        }

        const result = yield call(dispatchAndAwait, actionCreator, entry.payload);

        if (result.error) {
            const status = result.payload?.status;
            if (status >= 400 && status < 500) {
                // 4xx — server deliberately rejected, retrying won't help — delete it
                yield call(deleteActionQueueEntry, entry.id);
                logger.error(`❌ Action ${entry.actionName} rejected by server (${status}) — discarded`);
                yield put(replayFailure(`${entry.actionName} failed: ${result.payload?.message || status}`));
                return;
            }
            // 5xx or network error — keep entry so it retries on next reconnect
            logger.warn(`⚠️ Action ${entry.actionName} failed (${status || 'network'}) — will retry`);
            return;
        }

        yield call(deleteActionQueueEntry, entry.id);
        logger.info(`✅ Action queue replayed: ${entry.actionName}`);
    }
}

// Helpers — plain functions so saga can call them with yield call()
function getOutboxEntries() {
    return db.outbox.orderBy('timestamp').toArray();
}

function deleteOutboxEntry(id) {
    return db.outbox.delete(id);
}

function updateOutboxEntry(id, updates) {
    return db.outbox.update(id, updates);
}

function getOutboxCount() {
    return db.outbox.count();
}

function createOnlineChannel() {
    return eventChannel((emit) => {
        const handler = () => emit(true);
        window.addEventListener('online', handler);
        return () => window.removeEventListener('online', handler);
    });
}

function* replayOutboxSaga() {
    // Action queue runs regardless of whether the HTTP outbox has entries
    yield call(replayActionQueue);

    const entries = yield call(getOutboxEntries);
    if (!entries.length) return;

    yield put(replayStart());

    const keycloak = getKeycloak();

    try {
        yield call([keycloak, keycloak.updateToken], -1);
        logger.info('✅ Offline token exchanged for fresh access token before outbox replay');
    } catch (err) {
        logger.error('❌ Token refresh failed before outbox replay:', err);
        yield put(replayFailure('Token refresh failed — please log in again'));
        return;
    }

    for (const entry of entries) {
        let retries = 0;
        let done = false;

        while (!done && retries <= 3) {
            try {
                const storedHeaders = JSON.parse(entry.headers || '{}');
                const headersWithoutAuth = Object.fromEntries(
                    Object.entries(storedHeaders).filter(([k]) => k.toLowerCase() !== 'authorization')
                );
                const headers = {
                    ...headersWithoutAuth,
                    Authorization: `Bearer ${keycloak.token}`,
                    'Content-Type': 'application/json',
                    'X-Offline-Replay': 'true',
                };

                const response = yield call(fetch, entry.url, {
                    method: entry.method,
                    headers,
                    body: entry.body || undefined,
                });

                if (response.ok) {
                    yield call(deleteOutboxEntry, entry.id);
                    logger.info(`✅ Outbox entry ${entry.id} replayed successfully`);
                    done = true;
                } else if (response.status >= 400 && response.status < 500) {
                    // 4xx — do not retry; mark failed and stop the entire replay
                    yield call(updateOutboxEntry, entry.id, { status: 'failed' });
                    const msg = `Sync failed for ${entry.method} ${entry.url} — server rejected with ${response.status}`;
                    logger.error(`❌ ${msg}`);
                    yield put(replayFailure(msg));
                    done = true;
                    // Stop processing the rest of the queue to preserve order
                    const remaining = yield call(getOutboxCount);
                    yield put(setOutboxCount(remaining));
                    return;
                } else {
                    throw new Error(`Server error ${response.status}`);
                }
            } catch (err) {
                retries++;
                if (retries > 3) {
                    yield call(updateOutboxEntry, entry.id, { status: 'failed' });
                    logger.error(`❌ Outbox entry ${entry.id} failed after 3 retries:`, err);
                    done = true;
                } else {
                    logger.warn(`⚠️ Outbox entry ${entry.id} retry ${retries}/3 in ${2 ** retries}s`);
                    yield delay(2 ** retries * 1000);
                }
            }
        }
    }

    const remaining = yield call(getOutboxCount);
    yield put(setOutboxCount(remaining));
    if ('clearAppBadge' in navigator) navigator.clearAppBadge();
    yield put(replaySuccess());
    logger.info('✅ Outbox replay complete');
}

function* watchOnlineStatus() {
    const channel = createOnlineChannel();
    while (true) {
        yield take(channel);
        yield put(setOnlineStatus(true));
        yield call(replayOutboxSaga);
    }
}

export default function* offlineSaga() {
    yield fork(watchOnlineStatus);
    yield takeLatest(triggerOutboxReplay.type, replayOutboxSaga);
}
